import { useState, useRef, useEffect } from 'react'
import { MessageCircle, Search, Archive, Pin, Check, Send, ChevronDown, Phone, Smile, Paperclip, Image, FileText, Video, Mic, Download, X } from 'lucide-react'
import Picker from '@emoji-mart/react'
import data from '@emoji-mart/data'
import {
  useWaConversations,
  useWaConversationMessages,
  useMarkWaConversationAsRead,
  useArchiveWaConversation,
  useUnarchiveWaConversation,
  usePinWaConversation,
  useUnpinWaConversation,
  useSendWaMessage,
  useWaContactProfilePicture,
} from '@hooks/useWaMessages'
import { useWhatsAppMailboxes } from '@hooks/useWhatsAppMailboxes'
import { useUIStore } from '@stores/ui'
import { WaConversation, WaMessage, WhatsAppMailbox } from '@/types'
import { cn } from '@lib/utils'
import { api } from '@services/api'

// Message Content component to handle different media types
function MessageContent({
  message,
  isOutbound,
}: {
  message: WaMessage
  isOutbound: boolean
}) {
  const textColorClass = isOutbound ? 'text-white' : 'text-gray-900'
  const subtleTextClass = isOutbound ? 'text-green-200' : 'text-gray-500'

  // Helper to render media icon placeholder when no URL
  const renderMediaPlaceholder = (icon: React.ReactNode, label: string) => (
    <div className={cn('flex items-center gap-2 py-2', subtleTextClass)}>
      {icon}
      <span className="text-sm italic">{label}</span>
    </div>
  )

  switch (message.type) {
    case 'IMAGE':
      if (message.mediaUrl) {
        return (
          <div className="space-y-2">
            <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={message.mediaUrl}
                alt="Image"
                className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                style={{ maxHeight: '300px' }}
              />
            </a>
            {message.content && <p className={cn('text-sm', textColorClass)}>{message.content}</p>}
          </div>
        )
      }
      return (
        <div className="space-y-1">
          {renderMediaPlaceholder(<Image size={20} />, '[Image]')}
          {message.content && <p className={cn('text-sm', textColorClass)}>{message.content}</p>}
        </div>
      )

    case 'VIDEO':
      if (message.mediaUrl) {
        return (
          <div className="space-y-2">
            <video
              src={message.mediaUrl}
              controls
              className="max-w-full rounded-lg"
              style={{ maxHeight: '300px' }}
            />
            {message.content && <p className={cn('text-sm', textColorClass)}>{message.content}</p>}
          </div>
        )
      }
      return (
        <div className="space-y-1">
          {renderMediaPlaceholder(<Video size={20} />, '[Video]')}
          {message.content && <p className={cn('text-sm', textColorClass)}>{message.content}</p>}
        </div>
      )

    case 'AUDIO':
      if (message.mediaUrl) {
        return (
          <audio src={message.mediaUrl} controls className="max-w-full" />
        )
      }
      return renderMediaPlaceholder(<Mic size={20} />, '[Voice message]')

    case 'DOCUMENT':
      return (
        <div className={cn('flex items-center gap-3 py-1', textColorClass)}>
          <div className={cn('p-2 rounded-lg', isOutbound ? 'bg-green-700' : 'bg-gray-200')}>
            <FileText size={24} className={isOutbound ? 'text-white' : 'text-gray-600'} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{message.content || 'Document'}</p>
            {message.mediaUrl && (
              <a
                href={message.mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn('text-xs flex items-center gap-1 hover:underline', subtleTextClass)}
              >
                <Download size={12} /> Download
              </a>
            )}
          </div>
        </div>
      )

    case 'STICKER':
      if (message.mediaUrl) {
        return (
          <img
            src={message.mediaUrl}
            alt="Sticker"
            className="max-w-[150px]"
          />
        )
      }
      return renderMediaPlaceholder(<span className="text-2xl">🎭</span>, '[Sticker]')

    case 'LOCATION':
      return (
        <div className={cn('flex items-center gap-2', textColorClass)}>
          <span className="text-xl">📍</span>
          <span className="text-sm">{message.content || 'Location shared'}</span>
        </div>
      )

    case 'CONTACT':
      return (
        <div className={cn('flex items-center gap-2', textColorClass)}>
          <span className="text-xl">👤</span>
          <span className="text-sm">{message.content || 'Contact shared'}</span>
        </div>
      )

    case 'TEXT':
    default:
      return <p className={cn('text-sm', textColorClass)}>{message.content}</p>
  }
}

// Contact Avatar component with profile picture support
function ContactAvatar({
  contactId,
  accountId,
  name,
  phoneNumber,
  size = 'md',
}: {
  contactId: number
  accountId?: number
  name?: string | null
  phoneNumber?: string | null
  size?: 'sm' | 'md' | 'lg'
}) {
  const { data: profileData } = useWaContactProfilePicture(contactId, accountId)
  const displayName = name || phoneNumber || '?'
  const initial = displayName[0].toUpperCase()

  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
  }

  if (profileData?.profilePictureUrl) {
    return (
      <img
        src={profileData.profilePictureUrl}
        alt={displayName}
        className={cn(sizeClasses[size], 'rounded-full object-cover flex-shrink-0')}
        onError={(e) => {
          // Fallback to initials if image fails to load
          e.currentTarget.style.display = 'none'
          e.currentTarget.nextElementSibling?.classList.remove('hidden')
        }}
      />
    )
  }

  return (
    <div
      className={cn(
        sizeClasses[size],
        'rounded-full bg-green-100 flex items-center justify-center flex-shrink-0'
      )}
    >
      <span className="text-green-700 font-medium">{initial}</span>
    </div>
  )
}

export function WaInboxPage() {
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<WaConversation | null>(null)
  const [messageText, setMessageText] = useState('')
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch WhatsApp accounts for the selector
  const { data: mailboxesData } = useWhatsAppMailboxes()
  const mailboxes = mailboxesData?.data || []

  const { data: conversations, isLoading: conversationsLoading } = useWaConversations({
    search,
    isArchived: showArchived,
    accountId: selectedAccountId || undefined,
    limit: 50,
  })

  const { data: messages, isLoading: messagesLoading } = useWaConversationMessages(
    selectedConversation?.contactId || 0,
    selectedConversation?.accountId || 0,
    { limit: 50 }
  )

  const markAsRead = useMarkWaConversationAsRead()
  const archiveConversation = useArchiveWaConversation()
  const unarchiveConversation = useUnarchiveWaConversation()
  const pinConversation = usePinWaConversation()
  const unpinConversation = useUnpinWaConversation()
  const sendMessage = useSendWaMessage()
  const showToast = useUIStore((state) => state.showToast)

  // Auto-scroll to bottom when messages change or conversation is selected
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages?.data])

  const handleSelectConversation = async (conversation: WaConversation) => {
    setSelectedConversation(conversation)
    if (conversation.unreadCount > 0) {
      await markAsRead.mutateAsync(conversation.id)
    }
  }

  // File selection handler
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setFilePreview(event.target?.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      setFilePreview(null)
    }
  }

  // Clear selected file
  const clearSelectedFile = () => {
    setSelectedFile(null)
    setFilePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Determine message type from file
  const getMessageTypeFromFile = (file: File): 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' => {
    if (file.type.startsWith('image/')) return 'IMAGE'
    if (file.type.startsWith('video/')) return 'VIDEO'
    if (file.type.startsWith('audio/')) return 'AUDIO'
    return 'DOCUMENT'
  }

  const handleSendMessage = async () => {
    if ((!messageText.trim() && !selectedFile) || !selectedConversation) return

    try {
      setIsUploading(true)

      let mediaUrl: string | undefined
      let messageType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' = 'TEXT'

      // If there's a file, upload it first
      if (selectedFile) {
        messageType = getMessageTypeFromFile(selectedFile)

        // Upload file to get URL
        const formData = new FormData()
        formData.append('file', selectedFile)
        formData.append('accountId', selectedConversation.accountId.toString())
        formData.append('contactId', selectedConversation.contactId.toString())

        const response = await api.post('/whatsapp/messages/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        mediaUrl = response.data?.data?.mediaUrl
      }

      await sendMessage.mutateAsync({
        accountId: selectedConversation.accountId,
        contactId: selectedConversation.contactId,
        type: messageType,
        content: messageText || '', // Only use actual caption text, not filename
        mediaUrl,
      })

      setMessageText('')
      clearSelectedFile()
    } catch (error) {
      console.error('Failed to send message:', error)
      showToast('Failed to send message. Please try again.', 'error')
    } finally {
      setIsUploading(false)
    }
  }

  const handleEmojiSelect = (emoji: { native: string }) => {
    setMessageText((prev) => prev + emoji.native)
    setShowEmojiPicker(false)
  }

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false)
      }
    }
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEmojiPicker])

  const handleArchive = async (conversation: WaConversation) => {
    if (conversation.isArchived) {
      await unarchiveConversation.mutateAsync(conversation.id)
    } else {
      await archiveConversation.mutateAsync(conversation.id)
    }
    // Clear selection if archiving current conversation
    if (selectedConversation?.id === conversation.id) {
      setSelectedConversation(null)
    }
  }

  const handlePin = async (conversation: WaConversation) => {
    if (conversation.isPinned) {
      await unpinConversation.mutateAsync(conversation.id)
    } else {
      await pinConversation.mutateAsync(conversation.id)
    }
  }

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageCircle size={32} className="text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">WhatsApp Inbox</h1>
        </div>
        {/* WhatsApp Account Selector */}
        <div className="relative">
          <button
            onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[200px]"
          >
            <Phone size={18} className="text-green-600" />
            <span className="flex-1 text-left text-sm font-medium text-gray-700 truncate">
              {selectedAccountId
                ? mailboxes.find((m: WhatsAppMailbox) => m.id === selectedAccountId)?.phoneNumber || 'Unknown'
                : 'All Accounts'}
            </span>
            <ChevronDown
              size={16}
              className={cn('text-gray-400 transition-transform', accountDropdownOpen && 'rotate-180')}
            />
          </button>
          {accountDropdownOpen && (
            <>
              {/* Backdrop to close dropdown on click outside */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setAccountDropdownOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <div className="text-xs font-medium text-gray-500 uppercase px-2 py-1">
                    Select WhatsApp Account
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {/* All Accounts option */}
                  <button
                    onClick={() => {
                      setSelectedAccountId(null)
                      setAccountDropdownOpen(false)
                      setSelectedConversation(null)
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors',
                      selectedAccountId === null && 'bg-green-50'
                    )}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <Phone size={16} className="text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">All Accounts</div>
                      <div className="text-xs text-gray-500">View all conversations</div>
                    </div>
                    {selectedAccountId === null && (
                      <Check size={16} className="text-green-600 flex-shrink-0" />
                    )}
                  </button>
                  {/* Individual accounts */}
                  {mailboxes.map((mailbox: WhatsAppMailbox) => (
                    <button
                      key={mailbox.id}
                      onClick={() => {
                        setSelectedAccountId(mailbox.id)
                        setAccountDropdownOpen(false)
                        setSelectedConversation(null)
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-t border-gray-100',
                        selectedAccountId === mailbox.id && 'bg-green-50'
                      )}
                    >
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center',
                          mailbox.isConnected ? 'bg-green-100' : 'bg-red-100'
                        )}
                      >
                        <Phone
                          size={16}
                          className={mailbox.isConnected ? 'text-green-600' : 'text-red-500'}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {mailbox.displayName || mailbox.name || mailbox.phoneNumber}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <span>{mailbox.phoneNumber}</span>
                          <span
                            className={cn(
                              'inline-block w-2 h-2 rounded-full',
                              mailbox.isConnected ? 'bg-green-500' : 'bg-red-500'
                            )}
                          />
                        </div>
                      </div>
                      {selectedAccountId === mailbox.id && (
                        <Check size={16} className="text-green-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                  {mailboxes.length === 0 && (
                    <div className="px-4 py-6 text-center text-gray-500 text-sm">
                      No WhatsApp accounts connected
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex h-full gap-4">
        {/* Conversations List */}
        <div className="w-96 flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => setShowArchived(false)}
                className={cn(
                  'px-3 py-1 text-sm rounded-full',
                  !showArchived ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                )}
              >
                Active
              </button>
              <button
                onClick={() => setShowArchived(true)}
                className={cn(
                  'px-3 py-1 text-sm rounded-full',
                  showArchived ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                )}
              >
                Archived
              </button>
            </div>
          </div>

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto">
            {conversationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
              </div>
            ) : !conversations?.data?.length ? (
              <div className="text-center py-12 text-gray-500">
                <MessageCircle size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No conversations yet</p>
              </div>
            ) : (
              conversations.data.map((conversation: WaConversation) => (
                <div
                  key={conversation.id}
                  onClick={() => handleSelectConversation(conversation)}
                  className={cn(
                    'p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors',
                    selectedConversation?.id === conversation.id && 'bg-green-50',
                    conversation.isPinned && 'bg-yellow-50'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <ContactAvatar
                      contactId={conversation.contactId}
                      accountId={conversation.accountId}
                      name={conversation.contact?.name}
                      phoneNumber={conversation.contact?.phone}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 truncate">
                          {conversation.contact?.name || conversation.contact?.phone}
                        </span>
                        {conversation.lastMessageAt && (
                          <span className="text-xs text-gray-500">
                            {new Date(conversation.lastMessageAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {conversation.lastMessage?.content || 'No messages'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {conversation.isPinned && (
                          <Pin size={12} className="text-yellow-600" />
                        )}
                        {conversation.unreadCount > 0 && (
                          <span className="px-1.5 py-0.5 bg-green-600 text-white text-xs rounded-full">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Thread */}
        <div className="flex-1 flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
          {selectedConversation ? (
            <>
              {/* Conversation Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ContactAvatar
                    contactId={selectedConversation.contactId}
                    accountId={selectedConversation.accountId}
                    name={selectedConversation.contact?.name}
                    phoneNumber={selectedConversation.contact?.phone}
                  />
                  <div>
                    <div className="font-medium text-gray-900">
                      {selectedConversation.contact?.name || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {selectedConversation.contact?.phone}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePin(selectedConversation)}
                    className={cn(
                      'p-2 rounded-lg',
                      selectedConversation.isPinned
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'hover:bg-gray-100 text-gray-500'
                    )}
                    title={selectedConversation.isPinned ? 'Unpin' : 'Pin'}
                  >
                    <Pin size={18} />
                  </button>
                  <button
                    onClick={() => handleArchive(selectedConversation)}
                    className={cn(
                      'p-2 rounded-lg',
                      selectedConversation.isArchived
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-500'
                    )}
                    title={selectedConversation.isArchived ? 'Unarchive' : 'Archive'}
                  >
                    <Archive size={18} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                  </div>
                ) : !messages?.data?.length ? (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-sm">No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <>
                    {messages.data.map((message: WaMessage) => (
                      <div
                        key={message.id}
                        className={cn(
                          'flex',
                          message.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        <div
                          className={cn(
                            'max-w-[70%] rounded-lg px-4 py-2',
                            message.direction === 'OUTBOUND'
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          )}
                        >
                          <MessageContent
                            message={message}
                            isOutbound={message.direction === 'OUTBOUND'}
                          />
                          <div
                            className={cn(
                              'flex items-center gap-1 mt-1 text-xs',
                              message.direction === 'OUTBOUND' ? 'text-green-200' : 'text-gray-500'
                            )}
                          >
                            <span>{new Date(message.createdAt).toLocaleTimeString()}</span>
                            {message.direction === 'OUTBOUND' && message.status === 'READ' && (
                              <Check size={12} className="text-blue-300" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Scroll anchor for auto-scroll */}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                {/* File Preview */}
                {selectedFile && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      {filePreview ? (
                        <img
                          src={filePreview}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                          <FileText size={24} className="text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={clearSelectedFile}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full"
                        title="Remove file"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  {/* Attachment Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Attach file"
                  >
                    <Paperclip size={20} />
                  </button>
                  {/* Emoji Picker Button */}
                  <div className="relative" ref={emojiPickerRef}>
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Add emoji"
                    >
                      <Smile size={20} />
                    </button>
                    {/* Emoji Picker Popup */}
                    {showEmojiPicker && (
                      <div className="absolute bottom-12 left-0 z-50">
                        <Picker
                          data={data}
                          onEmojiSelect={handleEmojiSelect}
                          theme="light"
                          previewPosition="none"
                          skinTonePosition="none"
                          maxFrequentRows={2}
                        />
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !isUploading && handleSendMessage()}
                    placeholder={selectedFile ? 'Add a caption (optional)...' : 'Type a message...'}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={(!messageText.trim() && !selectedFile) || sendMessage.isPending || isUploading}
                    className="p-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUploading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <Send size={20} />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle size={48} className="mx-auto mb-4 text-gray-300" />
                <p>Select a conversation to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
